import { useEffect, useMemo, useState } from "react";
import PortalLayout from "@/pages/portal/PortalLayout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, RefreshCw, Receipt, Clock, CheckCircle2, AlertCircle } from "lucide-react";

const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const STATUS_META = {
  draft: { label: "Draft", color: "#6B7280", bg: "rgba(107,114,128,0.14)" },
  sent: { label: "Sent", color: "#3d7f9c", bg: "rgba(61,127,156,0.14)" },
  paid: { label: "Paid", color: "#1a4a1a", bg: "rgba(74,124,62,0.16)" },
  overdue: { label: "Overdue", color: "#8a1e1e", bg: "rgba(155,52,52,0.16)" },
  partial: { label: "Partial", color: "#946600", bg: "rgba(200,162,74,0.16)" },
  void: { label: "Void", color: "#666", bg: "rgba(0,0,0,0.08)" },
};

const TYPE_LABEL = {
  invoice: "Invoice",
  quote: "Quote",
  proforma: "Proforma",
  credit_note: "Credit Note",
  deposit: "Deposit",
};

const PortalInvoices = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = typeof window !== "undefined" ? localStorage.getItem("flowtalis_token") : null;

  const load = () => {
    setLoading(true);
    api.get("/portal/invoices")
      .then((r) => setItems(r.data.invoices || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const outstanding = items
      .filter((i) => (i.type === "invoice" || i.type === "deposit" || i.type === "proforma") &&
        ["sent", "overdue", "partial"].includes(i.status))
      .reduce((s, i) => s + (i.total - (i.paid_amount || 0)), 0);
    const paid = items.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);
    const overdue = items.filter((i) => i.status === "overdue").length;
    return { outstanding, paid, overdue };
  }, [items]);

  return (
    <PortalLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Client Portal</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="portal-invoices-title">
            Invoices
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Every invoice, quote and receipt in one place. Download PDF copies for your records anytime.
          </p>
        </div>
        <button onClick={load} className="ft-btn-ghost" data-testid="portal-invoices-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="ft-card" data-testid="portal-invoice-stat-outstanding">
          <div className="ft-eyebrow flex items-center gap-1.5"><Clock size={12} /> Outstanding</div>
          <div className="text-3xl font-bold tracking-tight mt-2">{money(stats.outstanding)}</div>
        </div>
        <div className="ft-card">
          <div className="ft-eyebrow flex items-center gap-1.5"><CheckCircle2 size={12} /> Paid to Date</div>
          <div className="text-3xl font-bold tracking-tight mt-2">{money(stats.paid)}</div>
        </div>
        <div className="ft-card">
          <div className="ft-eyebrow flex items-center gap-1.5"><AlertCircle size={12} /> Overdue</div>
          <div className="text-3xl font-bold tracking-tight mt-2">{stats.overdue}</div>
        </div>
      </div>

      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : items.length === 0 ? (
        <div className="ft-card text-center py-16" data-testid="portal-invoices-empty">
          <Receipt size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
          <h3 className="text-lg font-semibold">No invoices yet.</h3>
          <p className="text-[var(--ft-muted)] mt-2 max-w-md mx-auto">
            Once your FLOWTALIS team issues an invoice or quote it will appear here for download.
          </p>
        </div>
      ) : (
        <div className="grid gap-2" data-testid="portal-invoices-list">
          {items.map((inv) => {
            const meta = STATUS_META[inv.status] || STATUS_META.draft;
            const pdfUrl = `${API}/portal/invoices/${inv.id}/pdf?auth=${encodeURIComponent(token || "")}`;
            return (
              <div key={inv.id} className="ft-card flex items-center justify-between gap-4 flex-wrap"
                   data-testid={`portal-invoice-${inv.id}`}>
                <div className="min-w-0">
                  <div className="ft-eyebrow">{TYPE_LABEL[inv.type] || inv.type} · {inv.number}</div>
                  <div className="text-lg font-bold tracking-tight mt-1">
                    {money(inv.total)}
                    {inv.paid_amount ? <span className="text-sm text-[var(--ft-muted)] font-normal"> · Paid {money(inv.paid_amount)}</span> : null}
                  </div>
                  <div className="text-xs text-[var(--ft-muted)] mt-1">
                    {inv.project_name ? `${inv.project_name} · ` : ""}
                    Issued {(inv.created_at || "").slice(0, 10)}
                    {inv.due_date ? ` · Due ${inv.due_date}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: meta.bg, color: meta.color }}>
                    {meta.label}
                  </span>
                  <a href={pdfUrl} target="_blank" rel="noreferrer" className="ft-btn-secondary" data-testid={`portal-invoice-pdf-${inv.id}`}>
                    <Download size={14} /> PDF
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PortalLayout>
  );
};

export default PortalInvoices;
